"use client"

import styled from "@emotion/styled"
import { GetComponentQuery, GetPageByRouteQuery } from "@eurodrive/apollo"
import { FormBlock, PreviouslyViewed } from "@eurodrive/ui"
import { CarPageInfo, getBrands, usePreviouslyViewed } from "@eurodrive/utils"
import { LeasingCarDto } from "api"
import { useEffect, useRef } from "react"
import AccordionComponent from "../Accordion"
import Breadcrumbs from "../Breadcrumbs"
import { LeasingCarUmbraco } from "../extractUmbraco"
import Hero from "../Hero"
import InteriorCarousel from "../InteriorCarousel"
import StickyHeader from "../StickyHeader"
import SpinDetails from "./SpinDetails"

const StyledInnerContainer = styled.div`
  margin-top: 180px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: -28px;
  }
`

export default function LeasingPage({
  data,
  brands,
  carUmbraco,
  contactForm,
  carPageInfo,
  comparisonTexts,
}: {
  data: LeasingCarDto
  brands: GetComponentQuery
  carUmbraco?: LeasingCarUmbraco
  contactForm: GetPageByRouteQuery
  carPageInfo: CarPageInfo
  comparisonTexts: any
}) {
  const formRef = useRef<HTMLDivElement>(null)
  const brandsUmbraco = getBrands({ brands })
  const { addViewedCar } = usePreviouslyViewed<"Leasing">({
    brands: brandsUmbraco,
  })
  const contentForm = carUmbraco?.contentForm?.[0]

  const scrollToForm = () => {
    if (formRef.current) {
      const elementPosition = formRef.current.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - 150

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })

      // Focus on the first input field after scrolling
      setTimeout(() => {
        const firstInput = formRef.current?.querySelector(
          'input:not([type="hidden"]):not([disabled])',
        ) as HTMLInputElement | null
        firstInput?.focus()
      }, 500)
    }
  }

  useEffect(() => {
    addViewedCar(data, "Leasing")
  }, [])
  if (!data.carZone) return null
  return (
    <>
      <Hero
        mainImage={data.mainImage}
        desktopImage={carUmbraco?.heroBannerImageDesktop}
        mobileImage={carUmbraco?.heroBannerImageMobile}
      />
      <StickyHeader
        carName={data.modelName}
        leasing
        year={data.carZone.modelYear}
        description={data.types[0]?.vehicleTypeForWebsiteDescription}
        disclaimer={carPageInfo.legallyForComparison}
        onTestDriveClick={scrollToForm}
      />
      <StyledInnerContainer>
        <Breadcrumbs carName={data.modelName} />
        <SpinDetails
          data={data}
          desktopImages={carUmbraco?.gallery360ImagesDesktop}
          mobileImages={carUmbraco?.gallery360ImagesMobile}
        />
        <AccordionComponent
          showSpinCar={true}
          leasingData={data}
          leasingImage={carUmbraco?.vehicleInfoImage}
          data={data.carZone.spec}
          detailedImages={data.detailedImages}
          vehicleInfoBlockMapped={carPageInfo.vehicleInfoBlockMapped}
        />
        <InteriorCarousel
          pictures={data.detailedImages?.images}
          title={`${carPageInfo.vehicleGalleryTitle} ${data.modelName}`}
          leasingPictures={carUmbraco?.carouselPictures}
          vehicleId={`leasing-${data.manufacturerCode}-${data.modelCode}`}
        />
        <PreviouslyViewed
          brands={brandsUmbraco}
          title={carPageInfo.vehicleLastSeenCarTitle}
          comparisonBlock={comparisonTexts?.[0]}
        />

        <div ref={formRef}>
          <FormBlock
            contactForm={contactForm}
            carLeadType="leasing"
            modelName={data.carZone.name.modelName}
          />
        </div>
      </StyledInnerContainer>
    </>
  )
}
